/**
 * Integrytas Group - Contextual Online Help (OLH)
 * Page: OLH.MANAGER
 *
 * Setup interface for the help engine: authors olh_help_item plus per-locale
 * content (olh_help_item_tl), optional role targeting (olh_help_item_role), and
 * guided-tour steps (olh_tour_step / _tl). End users see this content through
 * useContextualHelp / the runtime components.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { useLookupNames } from '../../lib/use_lookup_names';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { hasAccess, getForensicActorId  } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';

const PAGE_CODE = 'OLH.MANAGER';
registerUdcHost(PAGE_CODE, 'olh_help_item', 'Help Items');

type HelpType = 'DOC' | 'TOOLTIP' | 'TOUR';
const LOCALES = ['EN_US', 'ES_MX'];

const ITEM_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code', type: 'text',   label: 'Internal Code', required: true, col_span: 1 },
  { key: 'help_type',     type: 'select', label: 'Help Type',     col_span: 1 },
  { key: 'screen_type',   type: 'select', label: 'Screen Type',   col_span: 1 },
  { key: 'screen_code',   type: 'text',   label: 'Screen Code',   col_span: 1 },
  { key: 'component_key', type: 'text',   label: 'Component Key', col_span: 1 },
  { key: 'field_key',     type: 'text',   label: 'Field Key',     col_span: 1 },
];
const ITEM_CORE_LAYOUT = toUldCoreLayout(ITEM_FIELD_DEFS);

const ITEM_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',   defaultWidth: 220 },
  { key: 'help_type',     header: 'Type',   defaultWidth: 120 },
  { key: 'screen',        header: 'Screen', defaultWidth: 200 },
  { key: 'anchor',        header: 'Anchor', defaultWidth: 200 },
  { key: 'is_active',     header: 'Active', defaultWidth: 90  },
];

interface ItemRow {
  olh_help_item_id: number; internal_code: string; help_type: HelpType;
  screen_type: 'APP' | 'CMS'; screen_code: string;
  component_key: string | null; field_key: string | null;
  sort_order: number; is_active: 'Y' | 'N'; description: string | null;
}
interface TlRow { locale_code: string; title: string | null; body_html: string | null }
interface RoleLite { role_id: number; internal_code: string }
interface StepRow {
  olh_tour_step_id: number; step_order: number; component_key: string | null;
  field_key: string | null; placement: string;
}

export interface OlhManagerPageProps { session: AuthSession }

export function OlhManagerPage({ session }: OlhManagerPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <OlhManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function OlhManagerPageInner({ session }: OlhManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const pageNames = useLookupNames('csm_page_master', 'csm_page_master_tl');
  const [rows, setRows] = useState<ItemRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ItemRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ItemRow | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the list's logical columns.
  const [facets, setFacets] = useState<{ help_type: string[]; screen_type: string[]; active: string[] }>(
    { help_type: [], screen_type: [], active: ['Y'] },
  );

  const udcGrid = useUdcGrid('olh_help_item', PAGE_CODE, { pkColumn: 'olh_help_item_id', rows: rows as unknown as Record<string, unknown>[] });
  const detail = useExpandedRows<number>();

  // §17.B13 related-children (expand/contract). A help item's translations, role
  // targets and tour steps are loaded in the modal, not per list row, so the panel
  // is inert here.
  // TODO: wire relations (surface role targets / tour steps once loaded per row).
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('olh_help_item')
        .select('olh_help_item_id, internal_code, help_type, screen_type, screen_code, component_key, field_key, sort_order, is_active, description')
        .order('screen_code').order('sort_order');
      if (e) throw e;
      setRows((data ?? []) as ItemRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const handleDelete = async (row: ItemRow) => {
    const { error: e } = await routedDelete(session, 'olh_help_item', 'olh_help_item_id', row.olh_help_item_id);
    if (e) { setError(e.message); return; }
    setDeleteTarget(null);
    void reload();
  };

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      help_type:   uniq(rows.map(r => r.help_type)).map(v => ({ value: v, label: v })),
      screen_type: uniq(rows.map(r => r.screen_type)).map(v => ({ value: v, label: v })),
      active: [{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }],
    };
  }, [rows, t]);

  const facetsActive = facets.help_type.length + facets.screen_type.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ help_type: [], screen_type: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const matchesFacets = (r: ItemRow) =>
      (facets.help_type.length   === 0 || facets.help_type.includes(r.help_type)) &&
      (facets.screen_type.length === 0 || facets.screen_type.includes(r.screen_type)) &&
      (facets.active.length      === 0 || facets.active.includes(r.is_active));
    const base = (!q ? rows : rows.filter(r =>
      r.internal_code.toLowerCase().includes(q) ||
      r.help_type.toLowerCase().includes(q) ||
      `${r.screen_type}:${r.screen_code}`.toLowerCase().includes(q) ||
      [r.component_key, r.field_key].filter(Boolean).join('.').toLowerCase().includes(q)))
      .filter(matchesFacets);
    return base.filter(r => udcGrid.matchRow(r as unknown as Record<string, unknown>));
  }, [rows, filter, facets, udcGrid]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('olh.heading', 'Contextual Help')}
          description={t('olh.list.description', 'Author help items, their per-locale content, role targeting and guided-tour steps.')}
          actions={
            <>
              <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('olh.refresh', 'Refresh')}
              </button>
              {canAdd && (<button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('olh.new_help_item', 'New Help Item')}
              </button>)}
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('olh.stat.total', 'Help items') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('olh.stat.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('olh.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('olh.filter_placeholder', 'Filter help items…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('olh.col.help_type', 'Type')} options={facetOptions.help_type} selected={facets.help_type} onChange={v => setFacets(f => ({ ...f, help_type: v }))} />
            <FacetFilter label={t('olh.field_screen_type', 'Screen Type')} options={facetOptions.screen_type} selected={facets.screen_type} onChange={v => setFacets(f => ({ ...f, screen_type: v }))} />
            <FacetFilter label={t('olh.col.is_active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('olh.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={[...ITEM_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('olh.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('olh.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('olh.col_actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.olh_help_item_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.olh_help_item_id)} hasChildren={hasRelated(r.olh_help_item_id)} onToggle={() => detail.toggle(r.olh_help_item_id)} expandLabel={t('olh.related.expand', 'Show related records')} collapseLabel={t('olh.related.collapse', 'Hide related')} tooltip={t('olh.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>}
                        {col.key === 'help_type'     && <span className="text-csm-xs">{r.help_type}</span>}
                        {col.key === 'screen'        && <span className="text-csm-xs">{r.screen_type}:{pageNames[r.screen_code] ?? r.screen_code}</span>}
                        {col.key === 'anchor'        && <span className="font-mono text-csm-2xs text-csm-ink-soft">{[r.component_key, r.field_key].filter(Boolean).join('.') || t('olh.anchor_page', '— page —')}</span>}
                        {col.key === 'is_active'     && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('olh.edit', 'Edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('olh.delete', 'Delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.olh_help_item_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.olh_help_item_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('olh.empty_title', 'No help items yet')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('olh.empty_new_help_item', '+ New Help Item')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <ItemModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('olh.delete_title', 'Delete Help Item?')}
          message={t('olh.delete_message', 'Delete "{{code}}"? All associated translations, role targets, and tour steps will also be removed. This cannot be undone.', { code: deleteTarget.internal_code })}
          onConfirm={() => void handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function ItemModal({ session, row, onClose, onSaved }: {
  session: AuthSession; row: ItemRow | null; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const actor = getForensicActorId(session);

  const initialValues = {
    code: row?.internal_code ?? '',
    helpType: (row?.help_type ?? 'TOOLTIP') as HelpType,
    screenType: (row?.screen_type ?? 'APP') as 'APP' | 'CMS',
    screenCode: row?.screen_code ?? '',
    componentKey: row?.component_key ?? '',
    fieldKey: row?.field_key ?? '',
    sortOrder: row?.sort_order ?? 0,
    active: row ? row.is_active === 'Y' : true,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const udcForm = useUdcFormFields('olh_help_item', PAGE_CODE, { pkColumn: 'olh_help_item_id', pk: row?.olh_help_item_id });
  const { layout } = useUldLayout('OLH.HELP_ITEM.MODAL', useMemo(() => [...ITEM_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]));
  const [tl, setTl] = useState<Record<string, TlRow>>({ EN_US: { locale_code: 'EN_US', title: '', body_html: '' }, ES_MX: { locale_code: 'ES_MX', title: '', body_html: '' } });
  const [roles, setRoles] = useState<RoleLite[]>([]);
  const [itemRoles, setItemRoles] = useState<{ olh_help_item_role_id: number; role_id: number }[]>([]);
  const [addRole, setAddRole] = useState('');
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  const itemId = row?.olh_help_item_id;

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);

  useEffect(() => {
    void apiDb.from('csm_role_master').select('role_id, internal_code').order('internal_code')
      .then(({ data }) => setRoles((data ?? []) as RoleLite[]));
    if (!itemId) return;
    void apiDb.from('olh_help_item_tl').select('locale_code, title, body_html').eq('olh_help_item_id', itemId)
      .then(({ data }) => {
        const next: Record<string, TlRow> = { EN_US: { locale_code: 'EN_US', title: '', body_html: '' }, ES_MX: { locale_code: 'ES_MX', title: '', body_html: '' } };
        (data ?? []).forEach((r: { locale_code: string; title: string | null; body_html: string | null }) => { next[r.locale_code] = { locale_code: r.locale_code, title: r.title ?? '', body_html: r.body_html ?? '' }; });
        setTl(next);
      });
    void apiDb.from('olh_help_item_role').select('olh_help_item_role_id, role_id').eq('olh_help_item_id', itemId)
      .then(({ data }) => setItemRoles((data ?? []) as { olh_help_item_role_id: number; role_id: number }[]));
    void apiDb.from('olh_tour_step').select('olh_tour_step_id, step_order, component_key, field_key, placement').eq('olh_help_item_id', itemId).order('step_order')
      .then(({ data }) => setSteps((data ?? []) as StepRow[]));
  }, [itemId]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const validateCode = (val: string) => {
    if (!val) { setInternalCodeError(null); return; }
    if (!/^[A-Z][A-Z0-9_.]*$/.test(val)) {
      setInternalCodeError(t('olh.err_code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
    } else {
      setInternalCodeError(null);
    }
  };

  const saveItem = async () => {
    setBusy(true); setError(null);
    try {
      if (isCreate && !form.code.trim()) throw new Error(t('olh.err_code_required', 'Internal code is required.'));
      if (internalCodeError) throw new Error(internalCodeError);
      if (!form.screenCode.trim()) throw new Error(t('olh.err_screen_required', 'Screen code is required.'));
      const payload = {
        help_type: form.helpType, screen_type: form.screenType, screen_code: form.screenCode.trim(),
        component_key: form.componentKey.trim() || null, field_key: form.fieldKey.trim() || null,
        sort_order: form.sortOrder, is_active: form.active ? 'Y' : 'N', description: null,
      };
      let id = itemId;
      if (isCreate) {
        const { row_id, error: e } = await routedInsert(session, 'olh_help_item', { internal_code: form.code.trim().toUpperCase(), ...payload });
        if (e) throw new Error(e.message);
        id = row_id ?? undefined;
      } else {
        const { error: e } = await routedUpdate(session, 'olh_help_item', 'olh_help_item_id', itemId, payload);
        if (e) throw new Error(e.message);
      }
      // Upsert translations that have content.
      const tlRows = LOCALES.map(l => tl[l]).filter(tr => (tr.title ?? '').trim() || (tr.body_html ?? '').trim())
        .map(tr => ({ olh_help_item_id: id, locale_code: tr.locale_code, title: tr.title || null, body_html: tr.body_html || null, modified_by: actor }));
      if (tlRows.length) {
        const { error: e } = await apiDb.from('olh_help_item_tl').upsert(tlRows, { onConflict: 'olh_help_item_id,locale_code' });
        if (e) throw e;
      }
      reset(form);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addRoleTarget = async () => {
    if (!itemId || !addRole) return;
    const { error: e } = await routedInsert(session, 'olh_help_item_role', { olh_help_item_id: itemId, role_id: Number(addRole) });
    if (e) { setError(e.message); return; }
    setAddRole('');
    const { data } = await apiDb.from('olh_help_item_role').select('olh_help_item_role_id, role_id').eq('olh_help_item_id', itemId);
    setItemRoles((data ?? []) as { olh_help_item_role_id: number; role_id: number }[]);
  };
  const removeRoleTarget = async (id: number) => {
    await routedDelete(session, 'olh_help_item_role', 'olh_help_item_role_id', id);
    setItemRoles(rs => rs.filter(r => r.olh_help_item_role_id !== id));
  };

  const addStep = async () => {
    if (!itemId) return;
    const order = (steps.length ? steps[steps.length - 1].step_order : 0) + 1;
    const { error: e } = await routedInsert(session, 'olh_tour_step', { olh_help_item_id: itemId, step_order: order, placement: 'auto' });
    if (e) { setError(e.message); return; }
    const { data } = await apiDb.from('olh_tour_step').select('olh_tour_step_id, step_order, component_key, field_key, placement').eq('olh_help_item_id', itemId).order('step_order');
    setSteps((data ?? []) as StepRow[]);
  };
  const saveStep = async (s: StepRow) => {
    await routedUpdate(session, 'olh_tour_step', 'olh_tour_step_id', s.olh_tour_step_id, { step_order: s.step_order, component_key: s.component_key || null, field_key: s.field_key || null, placement: s.placement });
  };
  const deleteStep = async (id: number) => {
    await routedDelete(session, 'olh_tour_step', 'olh_tour_step_id', id);
    setSteps(ss => ss.filter(s => s.olh_tour_step_id !== id));
  };

  const roleName = (id: number) => roles.find(r => r.role_id === id)?.internal_code ?? `role ${id}`;

  return (
    <>
      <Modal
        title={isCreate ? t('olh.modal_title_create', 'Create Help Item') : t('olh.modal_title_edit', 'Edit Help Item — {{code}}', { code: row?.internal_code ?? '' })}
        onClose={handleClose}
        width="2xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('olh.close', 'Close')}</button>
        }
      >
        <div className="mb-3">
          <FieldRenderer
            fields={useMemo(() => [...ITEM_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs])}
            layout={layout}
            columns={2}
            pageCode="OLH.HELP_ITEM.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'internal_code') return (
                <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('olh.field_internal_code', 'Internal Code')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('olh.field_internal_code', 'Internal Code')}</label>
                  <input type="text" value={form.code} disabled={!isCreate}
                         onChange={e => setForm(f2 => ({ ...f2, code: e.target.value.toUpperCase() }))}
                         onBlur={e => validateCode(e.target.value)}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('olh.code_hint', 'E.g. OLH.HELP.ITEM — uppercase, dots only')}</p>}
                </div>
                </PersonalizedField>
              );
              if (f.key === 'help_type') return (
                <PersonalizedField componentKey="modal" fieldKey="help_type" label={t('olh.field_help_type', 'Help Type')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('olh.field_help_type', 'Help Type')}</label>
                  <select value={form.helpType} onChange={e => setForm(f2 => ({ ...f2, helpType: e.target.value as HelpType }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">
                    <option value="TOOLTIP">TOOLTIP</option><option value="DOC">DOC</option><option value="TOUR">TOUR</option>
                  </select>
                </div>
                </PersonalizedField>
              );
              if (f.key === 'screen_type') return (
                <PersonalizedField componentKey="modal" fieldKey="screen_type" label={t('olh.field_screen_type', 'Screen Type')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('olh.field_screen_type', 'Screen Type')}</label>
                  <select value={form.screenType} onChange={e => setForm(f2 => ({ ...f2, screenType: e.target.value as 'APP' | 'CMS' }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">
                    <option value="APP">{t('olh.screen_type_app', 'APP (standard page)')}</option><option value="CMS">{t('olh.screen_type_cms', 'CMS (user-defined form)')}</option>
                  </select>
                </div>
                </PersonalizedField>
              );
              if (f.key === 'screen_code') return (
                <PersonalizedField componentKey="modal" fieldKey="screen_code" label={t('olh.field_screen_code', 'Screen Code')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('olh.field_screen_code', 'Screen Code')}</label>
                  <input type="text" value={form.screenCode} onChange={e => setForm(f2 => ({ ...f2, screenCode: e.target.value }))} placeholder="CSM.ROLE.LIST"
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none" />
                </div>
                </PersonalizedField>
              );
              if (f.key === 'component_key') return (
                <PersonalizedField componentKey="modal" fieldKey="component_key" label={t('olh.field_component_key', 'Component Key (optional)')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('olh.field_component_key', 'Component Key (optional)')}</label>
                  <input type="text" value={form.componentKey} onChange={e => setForm(f2 => ({ ...f2, componentKey: e.target.value }))} placeholder="drawer"
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none" />
                </div>
                </PersonalizedField>
              );
              if (f.key === 'field_key') return (
                <PersonalizedField componentKey="modal" fieldKey="field_key" label={t('olh.field_field_key', 'Field Key (optional)')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('olh.field_field_key', 'Field Key (optional)')}</label>
                  <input type="text" value={form.fieldKey} onChange={e => setForm(f2 => ({ ...f2, fieldKey: e.target.value }))} placeholder="internal_code"
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none" />
                </div>
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey="modal" fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>

        {LOCALES.map(loc => (
          <div key={loc} className="mb-3 rounded border border-csm-border-soft p-2">
            <div className="mb-1 text-csm-xs font-semibold text-csm-ink-soft">{loc}</div>
            <input type="text" value={tl[loc]?.title ?? ''} onChange={e => setTl(prev => ({ ...prev, [loc]: { ...prev[loc], locale_code: loc, title: e.target.value } }))}
                   placeholder={t('olh.tl_title_placeholder', 'Title')} className="mb-1 h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none" />
            <textarea rows={3} value={tl[loc]?.body_html ?? ''} onChange={e => setTl(prev => ({ ...prev, [loc]: { ...prev[loc], locale_code: loc, body_html: e.target.value } }))}
                      placeholder={t('olh.tl_body_placeholder', 'Body (rich HTML)')} className="w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs focus:border-csm-watermark focus:outline-none" />
          </div>
        ))}

        <div className="mb-3 flex items-end gap-3">
          <div>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('olh.sort', 'Sort')}</label>
            <input type="number" value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: Number(e.target.value) }))} className="h-8 w-20 rounded border border-csm-border px-2 text-csm-sm" />
          </div>
          <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-gray-700" /> {t('olh.active', 'Active')}
          </label>
          <button onClick={() => void saveItem()} disabled={busy} className="ml-auto h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('olh.saving', 'Saving…') : t('olh.save_item', 'Save Item')}</button>
        </div>

        {!isCreate && (
          <>
            <div className="mb-3 border-t border-csm-border pt-2">
              <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('olh.role_targeting', 'Role Targeting')} <span className="text-csm-2xs text-csm-watermark">{t('olh.role_targeting_hint', '(none = visible to all)')}</span></div>
              <div className="mb-2 flex flex-wrap gap-1">
                {itemRoles.map(ir => (
                  <span key={ir.olh_help_item_role_id} className="inline-flex items-center gap-1 rounded bg-csm-surface-alt px-2 py-0.5 text-csm-xs">
                    {roleName(ir.role_id)}
                    <button onClick={() => void removeRoleTarget(ir.olh_help_item_role_id)} className="text-csm-danger">×</button>
                  </span>
                ))}
                {itemRoles.length === 0 && <span className="text-csm-xs italic text-csm-watermark">{t('olh.visible_everyone', 'Visible to everyone.')}</span>}
              </div>
              <div className="flex gap-2">
                <select value={addRole} onChange={e => setAddRole(e.target.value)} className="h-8 w-48 rounded border border-csm-border px-2 text-csm-sm">
                  <option value="">{t('olh.add_role_option', '— add role —')}</option>
                  {roles.map(r => <option key={r.role_id} value={r.role_id}>{r.internal_code}</option>)}
                </select>
                <button onClick={() => void addRoleTarget()} disabled={!addRole} className="h-8 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-50">{t('olh.add', 'Add')}</button>
              </div>
            </div>

            {form.helpType === 'TOUR' && (
              <div className="mb-3 border-t border-csm-border pt-2">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('olh.tour_steps', 'Tour Steps')}</span>
                  <button onClick={() => void addStep()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('olh.add_step', '+ Step')}</button>
                  <span className="text-csm-2xs text-csm-watermark">{t('olh.tour_steps_hint', 'step content is edited per-locale in olh_tour_step_tl (EN fallback)')}</span>
                </div>
                {steps.map((s, idx) => (
                  <div key={s.olh_tour_step_id} className="mb-1 flex flex-wrap items-center gap-1">
                    <span className="w-6 text-csm-xs text-csm-watermark">#{s.step_order}</span>
                    <input value={s.component_key ?? ''} onChange={e => setSteps(ss => ss.map((x, i) => i === idx ? { ...x, component_key: e.target.value } : x))} onBlur={() => void saveStep(steps[idx])} placeholder={t('olh.step_component_placeholder', 'component')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                    <input value={s.field_key ?? ''} onChange={e => setSteps(ss => ss.map((x, i) => i === idx ? { ...x, field_key: e.target.value } : x))} onBlur={() => void saveStep(steps[idx])} placeholder={t('olh.step_field_placeholder', 'field')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                    <select value={s.placement} onChange={e => { setSteps(ss => ss.map((x, i) => i === idx ? { ...x, placement: e.target.value } : x)); }} onBlur={() => void saveStep(steps[idx])} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
                      {['auto', 'top', 'bottom', 'left', 'right', 'center'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <button onClick={() => void deleteStep(s.olh_tour_step_id)} className="text-csm-danger text-csm-xs">{t('olh.step_delete', 'delete')}</button>
                  </div>
                ))}
                {steps.length === 0 && <div className="text-csm-xs italic text-csm-watermark">{t('olh.no_steps', 'No steps yet.')}</div>}
              </div>
            )}
          </>
        )}


        {error && (
          <div className="mt-3">
            <ErrorBanner
              message={t('olh.save_failed', "Couldn't save the help item.")}
              detail={error}
              details_label={t('common.details', 'Details')}
            />
          </div>
        )}
        {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('olh.save_first', 'Save the item first to add role targeting and tour steps.')}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('olh.discard_title', 'Discard changes?')}
          message={t('olh.discard_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('olh.discard_confirm', 'Discard')}
          cancelLabel={t('olh.discard_cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(initialValues); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
